import { useState, useEffect, useRef } from 'react';
import { tools, toolCategories } from '@/data/siteData';
import { scrollReveal, staggerFadeIn, prefersReducedMotion } from '@/hooks/useGsap';

// Relational connection map for tool integration visualization
const toolConnections: Record<string, string[]> = {
  // AI Tools
  'ChatGPT': ['Make', 'Zapier', 'n8n'],
  'Claude': ['Make', 'n8n'],
  'Gemini': ['Make', 'n8n'],
  'Local LLM': ['n8n', 'Docker'],
  
  // Automation Tools
  'Make': ['ChatGPT', 'Claude', 'Gemini', 'GCP', 'AWS'],
  'Zapier': ['ChatGPT', 'Git'],
  'n8n': ['ChatGPT', 'Claude', 'Gemini', 'Local LLM', 'Docker', 'Git', 'CI/CD'],
  
  // Dev / Cloud Tools
  'Docker': ['n8n', 'Local LLM', 'CI/CD'],
  'Git': ['Zapier', 'n8n', 'CI/CD'],
  'CI/CD': ['n8n', 'Docker', 'Git', 'GCP', 'AWS'],
  'GCP': ['Make', 'CI/CD'],
  'AWS': ['Make', 'CI/CD']
};

const Tools = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);
  const [lines, setLines] = useState<{ x1: number; y1: number; x2: number; y2: number }[]>([]);

  useEffect(() => {
    if (sectionRef.current) {
      const titleEl = sectionRef.current.querySelector('.section-title');
      if (titleEl) scrollReveal(titleEl as HTMLElement);
    }
    if (categoriesRef.current) {
      const categories = categoriesRef.current.querySelectorAll('.tool-category');
      if (!prefersReducedMotion()) {
        staggerFadeIn(categories, { delay: 0.2, stagger: 0.15 });
      }
    }
  }, []);

  useEffect(() => {
    if (!hoveredTool || !containerRef.current || prefersReducedMotion()) {
      setLines([]);
      return;
    }

    const updateLines = () => {
      const parent = containerRef.current;
      if (!parent) return;
      const parentRect = parent.getBoundingClientRect();

      const getSlug = (name: string) => name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');

      const activeEl = document.getElementById("tool-" + getSlug(hoveredTool));
      if (!activeEl) return;
      const activeRect = activeEl.getBoundingClientRect();
      const x1 = activeRect.left - parentRect.left + activeRect.width / 2;
      const y1 = activeRect.top - parentRect.top + activeRect.height / 2;

      const targets = toolConnections[hoveredTool] || [];
      const newLines: typeof lines = [];

      targets.forEach(target => {
        const targetEl = document.getElementById("tool-" + getSlug(target));
        if (targetEl) {
          const targetRect = targetEl.getBoundingClientRect();
          const x2 = targetRect.left - parentRect.left + targetRect.width / 2;
          const y2 = targetRect.top - parentRect.top + targetRect.height / 2;
          newLines.push({ x1, y1, x2, y2 });
        }
      });

      // Reverse connections
      Object.entries(toolConnections).forEach(([source, dests]) => {
        if (dests.includes(hoveredTool) && source !== hoveredTool) {
          const sourceEl = document.getElementById("tool-" + getSlug(source));
          if (sourceEl) {
            const sourceRect = sourceEl.getBoundingClientRect();
            const x2 = sourceRect.left - parentRect.left + sourceRect.width / 2;
            const y2 = sourceRect.top - parentRect.top + sourceRect.height / 2;
            if (!newLines.some(l => l.x2 === x2 && l.y2 === y2)) {
              newLines.push({ x1, y1, x2, y2 });
            }
          }
        }
      });

      setLines(newLines);
    };

    updateLines();
    window.addEventListener('resize', updateLines);
    // Observe scroll/updates to redraw lines if layout shifts
    const observer = new ResizeObserver(updateLines);
    observer.observe(containerRef.current);

    return () => {
      window.removeEventListener('resize', updateLines);
      observer.disconnect();
    };
  }, [hoveredTool]);

  const groupedTools = {
    ai: tools.filter((t) => t.category === 'ai'),
    automation: tools.filter((t) => t.category === 'automation'),
    dev: tools.filter((t) => t.category === 'dev'),
  };

  const getBadgeClass = (toolName: string) => {
    let classes = "badge-tech transition-all duration-200 transform select-none cursor-pointer relative z-10 border rounded-none ";

    if (!hoveredTool) {
      return classes + "border-border";
    }

    if (hoveredTool === toolName) {
      classes += "bg-foreground text-background border-foreground scale-[1.05]";
    } else if (toolConnections[hoveredTool]?.includes(toolName) || toolConnections[toolName]?.includes(hoveredTool)) {
      classes += "bg-muted text-foreground border-border scale-[1.02]";
    } else {
      classes += "opacity-20 scale-[0.98] border-transparent";
    }

    return classes;
  };

  const getSlug = (name: string) => name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');

  return (
    <section id="tools" ref={sectionRef} className="section-padding section-atmosphere relative overflow-hidden border-t border-border">
      <div className="container-site max-w-4xl mx-auto">
        <div className="section-title text-center mb-12" style={{ opacity: prefersReducedMotion() ? 1 : 0 }}>
          <span className="chip mb-4">Tools</span>
          <h2 className="font-display uppercase font-light text-3xl sm:text-4xl tracking-[0.03em] text-foreground mb-4">
            Tech Stack
          </h2>
          <p className="text-muted-foreground text-xs leading-relaxed max-w-xl mx-auto uppercase tracking-wide">
            Tools yang sering gue pakai. Hover badge tools di bawah buat lihat visualisasi interaksinya!
          </p>
        </div>

        <div ref={containerRef} className="relative">
          {/* SVG Connector Lines Overlay */}
          {!prefersReducedMotion() && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0 overflow-visible">
              {lines.map((line, index) => {
                const dx = line.x2 - line.x1;
                const dy = line.y2 - line.y1;
                const length = Math.sqrt(dx * dx + dy * dy);
                const isVertical = Math.abs(dx) < 80;
                // Control point offsets for curve routing
                const midX = (line.x1 + line.x2) / 2;
                const midY = (line.y1 + line.y2) / 2 - (isVertical ? 0 : 25);
                const pathData = `M ${line.x1} ${line.y1} Q ${midX} ${midY} ${line.x2} ${line.y2}`;
                
                return (
                  <g key={index}>
                    <path
                      d={pathData}
                      fill="none"
                      stroke="hsl(var(--border) / 0.15)"
                      strokeWidth="1.5"
                    />
                    <path
                      d={pathData}
                      fill="none"
                      stroke="hsl(var(--primary))"
                      strokeWidth="1.5"
                      strokeDasharray={length}
                      strokeDashoffset={length}
                      style={{
                        animation: 'draw-line 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                      }}
                    />
                  </g>
                );
              })}
            </svg>
          )}

          <div ref={categoriesRef} className="grid sm:grid-cols-3 gap-6 relative z-10">
            {(Object.keys(toolCategories) as Array<keyof typeof toolCategories>).map(
              (category) => (
                <div
                  key={category}
                  className="tool-category min-h-full bg-card rounded-none p-6 border border-border"
                  style={{ opacity: prefersReducedMotion() ? 1 : 0 }}
                >
                  <div className="mb-4 border-b border-border pb-3">
                    <h3 className="font-sans uppercase font-bold text-xs tracking-wider text-foreground">
                      {toolCategories[category].label}
                    </h3>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mt-0.5">
                      {toolCategories[category].description}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {groupedTools[category].map((tool) => (
                      <span
                        key={tool.name}
                        id={"tool-" + getSlug(tool.name)}
                        className={getBadgeClass(tool.name)}
                        onMouseEnter={() => setHoveredTool(tool.name)}
                        onMouseLeave={() => setHoveredTool(null)}
                      >
                        {tool.name}
                      </span>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground uppercase tracking-widest mt-8 font-semibold">
          "Tools evolve — I focus on principles, not hype."
        </p>
      </div>
    </section>
  );
};

export default Tools;
